<script>
import debounce from 'lodash/debounce';
import Stripe from '@shared/services/Stripe';
import utilsMixin from '@shared/mixins/utils';
import StripeElementsSplitted from '@shared/components/StripeElementsSplitted.vue';
import { setTimeoutPromise } from '@shared/services/Utils';
import APIPayment from '@app/services/API/Payment';
import APIMemberships from '@app/services/API/Memberships';
import APIStore from '@app/services/API/Store';
import trainingsMixin from '@app/mixins/trainings';
import ListFeatures from '@app/components/ListFeatures.vue';
import UpdatePaymentMethod from '@app/components/UpdatePaymentMethod.vue';
import CancelSubscriptionForm from '@app/components/CancelSubscriptionForm.vue';
import ChurnConfirmation from '@app/components/ChurnConfirmation.vue';
import { api } from '@shared/config/axios';

const WEBHOOK_TIMEOUT = 3000;
const URL_FREQUENCIES = {
  MONTHLY: 'MONTHLY',
  MONTH: 'MONTHLY',
  MENSUEL: 'MONTHLY',
  YEARLY: 'YEARLY',
  YEAR: 'YEARLY',
  ANNUAL: 'YEARLY',
  ANNUEL: 'YEARLY',
};

export default {
  i18n: {
    messages: {
      fr: {
        messages: {
          forbidden_trainings: 'Vous avez des formations en statut "<strong>caché</strong>" ou "<strong>non répertorié</strong>". Ces statuts n\'étant pas disponibles pour le forfait DÉCOUVERTE, veuillez d\'abord supprimer ou publier les formations concernées.',
          forbidden_training_settings: '<strong>Vos paramètres actuels concernant les logs et temps de connexion</strong> (vérificateur d\'activité et comportement à adopter au changement d\'onglet) ne sont pas disponibles pour le forfait DÉCOUVERTE. Veuillez désactiver ces paramétrages dans <strong>Formations > Paramètres</strong>.',
          too_many_trainings_for_plan: 'Vous avez <strong>plus de 3 formations publiées</strong>, ce qui correspond au maximum de formations disponibles sur le forfait DÉCOUVERTE. Veuillez d\'abord supprimer ou mettre en brouillon les formations supplémentaires.',
        },
        exceptions: {
          store_has_active_customer_memberships: 'Vous ne pouvez pas annuler votre abonnement tant qu\'il existe des abonnements clients actifs. Veuillez annuler tous les abonnements actifs avant d\'annuler votre abonnement.',
          store_has_active_customer_memberships_title: 'Abonnements clients actifs',
        },
      },
      en: {
        exceptions: {
          store_has_active_customer_memberships: 'You cannot cancel your subscription while there are active customer memberships. Please cancel all active memberships before canceling your subscription.',
          store_has_active_customer_memberships_title: 'Active Customer Memberships',
        },
      },
    },
  },
  mixins: [trainingsMixin, utilsMixin],
  components: {
    ListFeatures,
    StripeElementsSplitted,
    UpdatePaymentMethod,
  },
  head: {
    title: 'Abonnement',
  },
  data() {
    return {
      isLoading: false,
      isFetching: false,
      isChangePaymentMethodOpen: false,
      stripeError: null,
      stripeIsCompleted: false,
      frequency: 'YEARLY',
      plan: 'PRO',
      discountIsChecking: false,
      discount: '',
      discountData: null,
      subscription: null,
      paymentMethods: null,
      churnDiscounts: {},
    };
  },
  computed: {
    authUser() {
      return this.$store.getters['auth/user'];
    },
    authStore() {
      return this.$store.getters['auth/store'];
    },
    hasProPlan() {
      return this.$store.getters['auth/hasProPlan'];
    },
    hasPerkPlan() {
      return this.$store.getters['auth/hasPerkPlan'];
    },
    hasPercentPlan() {
      return this.$store.getters['auth/hasPercentPlan'];
    },
    isNotPercentPlan() {
      return this.$store.getters['auth/isNotPercentPlan'];
    },
    subscriptionPlan() {
      if (!this.subscription.store_subscription) {
        return null;
      }

      const plan = this.subscription.store_subscription.items.find((v) => v.type !== 'USER_ACCOUNT');

      return plan;
    },
    subscriptionFrequency() {
      if (!this.subscription.store_subscription) {
        return null;
      }

      return this.subscription.store_subscription.frequency;
    },
    subscriptionStartDate() {
      if (!this.subscription.store_subscription) {
        return null;
      }

      const subscription = this.subscription.store_subscription.items.slice();
      subscription.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return subscription[0].created_at;
    },
    paymentMethodExp() {
      let month = this.paymentMethods[0].card_exp_month;
      const year = this.paymentMethods[0].card_exp_year;

      if (month == null || year == null) {
        return '';
      }

      month = month > 9 ? month : `0${month}`;

      return `${month} / ${year.toString().substring(2)}`;
    },
    price() {
      return this.$constants.PRICES[this.frequency][this.plan];
    },
    hasValidNewDiscount() {
      return this.discountData && ('code' in this.discountData);
    },
    hasDiscountOnSubscription() {
      if (!this.subscriptionStartDate) {
        return false;
      }

      const data = this.subscription.additional_data;

      return (data.percent_off > 0 || data.amount_off > 0);
    },
    cantSubscribe() {
      return (
        (this.subscriptionPlan && this.subscriptionPlan.type === this.plan)
        || (!this.stripeIsCompleted && !this.paymentMethods.length)
        || this.discountIsChecking
      );
    },
  },
  methods: {
    getQueryValue(key) {
      const value = this.$route.query[key];

      return Array.isArray(value) ? value[0] : value;
    },
    initializeSubscriptionLink() {
      const frequency = this.getQueryValue('frequency');
      const normalizedFrequency = typeof frequency === 'string'
        ? URL_FREQUENCIES[frequency.trim().toUpperCase()]
        : null;

      if (normalizedFrequency) {
        this.frequency = normalizedFrequency;
      }

      const code = this.getQueryValue('code');
      if (typeof code === 'string') {
        this.discount = code.trim().substring(0, 32);
      }
    },
    scrollToSubscriptionForm() {
      if (
        this.$route.query.onglet !== 'vous'
        || this.subscriptionStartDate
        || !this.$refs.payment_form
      ) {
        return;
      }

      this.$scrollTo(this.$refs.payment_form);
    },
    loadData() {
      this.isFetching = true;
      return Promise.all([
        this.loadSubscriptions(),
        this.loadPaymentMethods(),
        this.loadChurnDiscount(),
      ])
        .finally(() => {
          this.isFetching = false;

          if (this.subscriptionFrequency) {
            this.frequency = this.subscriptionFrequency;
          }
        });
    },
    contacterEquipePeda() {
      window.location.href = 'mailto:allo@teachizy.fr?subject=Consultation%20pédagogique';
    },
    contacterEquipeMarket() {
      window.location.href = 'mailto:allo@teachizy.fr?subject=Consultation%20marketing';
    },
    loadSubscriptions() {
      return APIPayment.getSubscriptions()
        .then(({ data }) => (this.subscription = data));
    },
    loadPaymentMethods() {
      return APIPayment.getPaymentMethods()
        .then(({ data }) => (this.paymentMethods = data));
    },
    loadChurnDiscount() {
      return APIPayment.getChurnDiscount()
        .then(({ data }) => (this.churnDiscounts = data));
    },
    onPaymentMethodChange(paymentMethods) {
      this.paymentMethods = paymentMethods;
      this.isChangePaymentMethodOpen = false;
    },
    handle() {
      const needsStripeCompleted = !this.paymentMethods.length && !this.stripeIsCompleted;

      if (needsStripeCompleted) {
        return;
      }

      if (this.subscriptionStartDate) {
        const loader = this.$buefy.loading.open();
        APIPayment.getSubscriptionProration({
          type: this.plan,
          frequency: this.subscriptionFrequency,
          promo_code: this.discount || undefined,
        })
          .then(({ data }) => {
            let price;
            const subscriptionData = this.subscription.additional_data;

            if (this.hasValidNewDiscount || this.hasDiscountOnSubscription) {
              const discountData = this.hasValidNewDiscount
                ? this.discountData
                : subscriptionData;

              price = discountData.percent_off > 0
                ? (this.price * (1 - discountData.percent_off / 100))
                : (this.price - discountData.amount_off);

              price = utilsMixin.filters.formatPrice(price, 'EUR');
            } else {
              price = utilsMixin.filters.formatPrice(this.price, 'EUR');
            }

            let prorataMessage;
            const currentPlanName = this.$t(`packs.${this.authStore.plan}`);

            if (data.next_payment_date) {
              prorataMessage = `
                <p>
                  Pour passer au nouveau Forfait, vous serez facturé immédiatement de
                  ${utilsMixin.filters.formatPrice(data.prorated_amount, 'EUR')} TTC
                  (prorata de votre période de facturation en cours),
                  puis de ${price} TTC à chaque date anniversaire de votre abonnement
                  (à partir du ${this.$moment.utc(data.next_payment_date).local().format('DD/MM/YY')})
                </p>
              `.trim();
            } else {
              prorataMessage = `
                <p>
                  En changeant de Forfait, vous perdrez immédiatement
                  <strong>tous vos avantages spécifiques au ${currentPlanName}</strong>.
                </p>
                <p>
                  Votre abonnement passera à ${price} à partir du
                  ${this.$moment.utc(subscriptionData.next_payment_date).local().format('DD/MM/YY')},
                  date de votre prochaine facture.
                </p>
              `.trim();
            }

            this.$buefy.dialog.confirm({
              message: `
                <div class="content">
                  ${prorataMessage}
                </div>
              `,
              confirmText: 'Souscrire',
              focusOn: 'cancel',
              onConfirm: () => this.subscribe(),
            });
          })
          .finally(() => loader.close());

        return;
      }

      this.subscribe();
    },
    subscribe() {
      let promise;
      const loader = this.$buefy.loading.open();
      this.isLoading = true;

      if (this.paymentMethods.length) {
        promise = APIPayment.subscribe({
          type: this.plan,
          frequency: this.frequency,
          promo_code: this.discount || undefined,
        });
      } else {
        promise = this.$refs.card.createPaymentMethod()
          .then((res) => APIPayment.subscribe({
            type: this.plan,
            frequency: this.frequency,
            payment_method: res.paymentMethod.id,
            promo_code: this.discount || undefined,
          }));
      }

      return promise.then(this.handleSubscription)
        .then(() => setTimeoutPromise(
          () => Promise.all([this.loadData(), this.$store.dispatch('auth/fetch')]),
          WEBHOOK_TIMEOUT,
        ))
        .then(() => {
          this.$showMessage.success();
          this.$scrollTo(this.$el);
        })
        .finally(() => {
          this.isLoading = false;
          loader.close();
        });
    },
    handleSubscription({
      data: {
        user, additional_data, store_subscription, subscription,
      },
    }) {
      this.$store.commit('auth/update', user);

      const { latest_invoice } = subscription;
      const { payment_intent } = latest_invoice;

      if (payment_intent) {
        const { client_secret, status } = payment_intent;

        if (status === 'requires_action' || status === 'requires_confirmation') {
          let confirmCardPayment;

          if (this.paymentMethods.length) {
            confirmCardPayment = Stripe.init(this.$env.stripePK)
              .then(() => Stripe.confirmCardPayment(client_secret, {
                payment_method: payment_intent.payment_method,
              }));
          } else {
            confirmCardPayment = this.$refs.card.confirmCardPayment(client_secret);
          }

          return confirmCardPayment.then(() => {
            this.$store.commit('auth/update', user);
            this.subscription = { additional_data, store_subscription };
          });
        }
      }

      this.$store.commit('auth/update', user);
      this.subscription = { additional_data, store_subscription };
      return null;
    },
    async unsubscribeStorePlan() {
      if (!this.subscriptionPlan) {
        return;
      }

      const loader = this.$buefy.loading.open();

      try {
        const { data } = await APIMemberships.checkActiveCustomerMemberships();

        if (data.has_active_memberships) {
          loader.close();
          this.$buefy.dialog.alert({
            type: 'is-danger',
            title: this.$t('exceptions.store_has_active_customer_memberships_title'),
            message: this.$t('exceptions.store_has_active_customer_memberships'),
            confirmText: 'OK',
            confirmButtonClass: 'is-primary',
          });
          return;
        }
      } catch (error) {
        console.error('Error checking active customer memberships:', error);
        loader.close();
        return;
      }

      const trainings = this.$store.state.trainings.list;

      if (!trainings) {
        this.$store.dispatch('trainings/fetch')
          .then((trainings) => {
            loader.close();
            this.confirmUnsubscribeStorePlan(trainings.data);
          })
          .catch(() => loader.close());

        return;
      }

      loader.close();
      this.confirmUnsubscribeStorePlan(trainings.data);
    },
    confirmUnsubscribeStorePlan(trainings) {
      const hasForbiddenTrainings = trainings.some((v) => ['UNLISTED', 'HIDDEN'].includes(v.status));

      if (hasForbiddenTrainings) {
        this.$buefy.dialog.alert(this.$t('messages.forbidden_trainings'));
        return;
      }

      const nbPublishedTrainings = trainings.filter((v) => v.status !== 'DRAFT').length;

      if (nbPublishedTrainings > 3) {
        this.$buefy.dialog.alert(this.$t('messages.too_many_trainings_for_plan'));
        return;
      }

      const trainingOptions = this.authStore.feature_options;
      if (trainingOptions && trainingOptions.customer_session
        && (trainingOptions.customer_session.inactivity_timeout != null
          || trainingOptions.customer_session.disabled_on_tab_quit)) {
        this.$buefy.dialog.alert(this.$t('messages.forbidden_training_settings'));
        return;
      }

      this.$buefy.dialog.confirm({
        type: 'is-danger',
        message: `
          <strong>La résiliation de votre abonnement sera immédiate</strong>
          et vous perdrez toutes les fonctionnalités du ${this.$t(`packs.${this.subscriptionPlan.type}`)} dès maintenant.<br>
          <ul>
            <li>Seuls vos <strong>70 premiers apprenants</strong> (tous statuts confondus) auront toujours accès à leurs formations.</li>
            <li>Seules vos <strong>3 plus anciennes formations actives</strong> resteront accessibles à vos apprenants.</li>
          </ul>
        `,
        focusOn: 'cancel',
        onConfirm: () => {
          if (!this.subscription.additional_data.has_churn_discount) {
            this.openChurnConfirmation();
          } else {
            this.openCancelSubscriptionForm();
          }
        },
      });
    },
    openChurnConfirmation() {
      let modal = this.$buefy.modal.open({
        parent: this,
        component: ChurnConfirmation,
        hasModalCard: true,
        props: {
          subscription: this.subscription,
          discount: this.subscription.store_subscription.frequency === 'MONTHLY' ? this.churnDiscounts.monthly : this.churnDiscounts.yearly,
          // subscriptionPlan: this.subscriptionPlan,
        },
        events: {
          applyDiscount: () => {
            const loader = this.$buefy.loading.open();
            api.$post('/churn-discounts')
              .then(({ data: { user, additional_data, store_subscription } }) => {
                this.$buefy.toast.open({
                  message: 'La réduction a été appliquée à votre abonnement',
                  type: 'is-success',
                });
                this.$emit('close');
                this.$store.commit('auth/update', user);
                this.subscription = { additional_data, store_subscription };

                Promise.all([
                  this.loadData(),
                  this.$store.dispatch('auth/fetch'),
                ]);
              })
              .catch((error) => {
                this.$buefy.toast.open({
                  message: 'Une erreur est survenue lors de l\'application de la réduction',
                  type: 'is-danger',
                });
                console.error('Error applying churn discount:', error);
              })
              .finally(() => {
                loader.close();
                modal.close();
              });
          },
          confirm: () => {
            modal.close();
            modal = this.openCancelSubscriptionForm();
          },
        },
      });
      return modal;
    },
    openCancelSubscriptionForm() {
      const modal = this.$buefy.modal.open({
        parent: this,
        component: CancelSubscriptionForm,
        hasModalCard: true,
        props: {
          subscriptionPlan: this.subscriptionPlan,
        },
        events: {
          confirm: ({ password, cancel_reason, cancel_additional_text } = {}) => {
            const loader = this.$buefy.loading.open();
            APIPayment.unsubscribe({
              password,
              cancel_reason,
              cancel_additional_text,
              subscriptionId: this.subscription.store_subscription.id,
              itemId: this.subscriptionPlan.id,
            })
              .then(({ data: { user, additional_data, store_subscription } }) => {
                this.$store.commit('auth/update', user);
                this.subscription = { additional_data, store_subscription };
                modal.close();
                return setTimeoutPromise(() => Promise.all([
                  this.loadData(),
                  this.$store.dispatch('auth/fetch'),
                ]), WEBHOOK_TIMEOUT);
              })
              .then(() => APIMemberships.checkActiveCustomerMemberships())
              .then(({ data }) => {
                if (data.has_active_memberships) {
                  this.showActiveMembershipsModal();
                }
              })
              .catch((error) => {
                this.$buefy.toast.open({
                  message: error.response?.data?.message || 'Une erreur est survenue',
                  type: 'is-danger',
                });
              })
              .finally(() => {
                loader.close();
              });
          },
        },
      });

      return modal;
    },
    showActiveMembershipsModal() {
      const loader = this.$buefy.loading.open();
      APIMemberships.getActiveCustomerMemberships()
        .then(({ data: activeMemberships }) => {
          loader.close();
          if (!activeMemberships || activeMemberships.length === 0) {
            return;
          }

          const membershipsList = activeMemberships.map((m) => {
            const customer = m.customer || {};
            const membership = m.membership || {};
            return `${customer.firstname || ''} ${customer.lastname || ''} - ${membership.name || ''}`;
          }).join('<br>');

          this.$buefy.dialog.confirm({
            type: 'is-warning',
            title: 'Abonnements clients actifs',
            message: `
              <div class="content">
                <p>Vous avez ${activeMemberships.length} abonnement(s) client(s) actif(s).</p>
                <p class="mt-3"><strong>Abonnements actifs :</strong></p>
                <div class="mt-2">${membershipsList}</div>
                <p class="mt-4">Que souhaitez-vous faire avec ces abonnements ?</p>
                <p class="mt-2"><strong>Mettre en pause</strong> : Les abonnements seront temporairement suspendus et pourront être réactivés plus tard.</p>
                <p class="mt-2"><strong>Annuler</strong> : Les abonnements seront annulés définitivement.</p>
              </div>
            `,
            confirmText: 'Mettre en pause',
            cancelText: 'Annuler',
            hasIcon: true,
            onConfirm: () => {
              this.pauseAllActiveMemberships(activeMemberships);
            },
            onCancel: () => {
              this.cancelAllActiveMemberships(activeMemberships);
            },
          });
        })
        .catch((error) => {
          loader.close();
          console.error('Error fetching active memberships:', error);
        });
    },
    async cancelAllActiveMemberships(activeMemberships) {
      this.$buefy.dialog.confirm({
        type: 'is-danger',
        title: 'Confirmer l\'annulation',
        message: 'Êtes-vous sûr de vouloir annuler définitivement tous ces abonnements ?',
        confirmText: 'Oui, annuler',
        cancelText: 'Non',
        hasIcon: true,
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          const storeUUID = this.authStore.uuid;
          const promises = activeMemberships.map((membership) => APIStore.cancelCustomerMembership(storeUUID, membership.uuid)
            .catch((error) => {
              console.error(`Error canceling membership ${membership.uuid}:`, error);
              return null;
            }));

          try {
            await Promise.all(promises);
            this.$buefy.toast.open({
              message: 'Les abonnements ont été annulés avec succès',
              type: 'is-success',
            });
          } catch (error) {
            this.$buefy.toast.open({
              message: 'Une erreur est survenue lors de l\'annulation des abonnements',
              type: 'is-danger',
            });
          } finally {
            loader.close();
          }
        },
      });
    },
    async pauseAllActiveMemberships(activeMemberships) {
      const loader = this.$buefy.loading.open();
      const storeUUID = this.authStore.uuid;
      const promises = activeMemberships.map((membership) => APIStore.pauseCustomerMembership(storeUUID, membership.uuid)
        .catch((error) => {
          console.error(`Error pausing membership ${membership.uuid}:`, error);
          return null;
        }));

      try {
        await Promise.all(promises);
        this.$buefy.toast.open({
          message: 'Les abonnements ont été mis en pause avec succès',
          type: 'is-success',
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Une erreur est survenue lors de la mise en pause des abonnements',
          type: 'is-danger',
        });
      } finally {
        loader.close();
      }
    },
    checkDiscount: debounce(function() {
      if (!this.discount) {
        this.discountData = null;
        return;
      }

      this.discountIsChecking = true;
      APIPayment.checkDiscount({ code: this.discount, type: this.plan, frequency: this.frequency })
        .then(({ data }) => (this.discountData = data))
        .finally(() => (this.discountIsChecking = false));
    }, 250),
    switchPlan(plan) {
      this.plan = plan;

      this.checkDiscount();
    },
    switchFrequency(frequency) {
      this.frequency = frequency;

      this.checkDiscount();
    },
  },
  created() {
    this.initializeSubscriptionLink();
    this.loadData()
      .finally(() => {
        if (this.discount) {
          this.checkDiscount();
        }

        this.$nextTick(() => this.scrollToSubscriptionForm());
      });
  },
};
</script>

<template>
  <div class="pcomp">
    <h2 class="title is-4 is-size-6-mobile mb-5 has-text-centered">
      Découvrez les avantages et les possibilités de Teachizy
    </h2>
    <p class="is-size-6 is-size-7-mobile mb-10 has-text-centered">
      Vous êtes actuellement sur le forfait
      <strong class="tdecoration-underline" v-t="`plans.${authStore.plan}`" />
    </p>

    <div class="tabs mb-10">
      <ul>
        <router-link tag="li" :to="{query: {onglet: 'vous'}}" exact-active-class="is-active">
          <a >Mon abonnement</a>
        </router-link>
        <router-link tag="li" :to="{query: {}}" exact-active-class="is-active">
          <a>Forfaits</a>
        </router-link>
        <router-link tag="li" :to="{query: {onglet: 'services'}}" exact-active-class="is-active">
          <a>Accompagnements</a>
        </router-link>
      </ul>
    </div>

    <AppFeatureWrapper v-if="$route.query.onglet == 'vous'" tag="section">
      <template #title>
        Gérez votre abonnement
      </template>

      <template #content>
        <p>
          Pour bénéficier des fonctionnalités avancées de Teachizy,
          nous vous proposons un <strong>forfait PRO</strong> ou un <strong>forfait EXPERT</strong>,
          tous les deux <strong>sans engagement</strong>.
        </p>
        <p>
          Nous ne stockons aucune carte bancaire dans notre système,
          les paiements sont traités par <strong>Stripe</strong>,
          un service <strong>sécurisé</strong> de paiement en ligne.
        </p>
        <p>
          Le <strong>forfait DÉCOUVERTE</strong>, qui est gratuit et sans engagement,
          restera disponible en cas de besoin.
        </p>
      </template>

      <template #form>
        <div v-if="hasPerkPlan" class="box is-warning">
          <div class="columns is-multiline is-vcentered">
            <p class="column">
              Pour débloquer <strong>TOUTES les fonctionnalités en illimité</strong>,
              vous pouvez souscrire au forfait PRO ou EXPERT
            </p>
            <div class="column is-narrow">
              <b-button type="is-warning" @click="$scrollTo($refs.payment_form)">
                Débloquer <b-icon class="has-text-black ml-1" icon="gem" />
              </b-button>
            </div>
          </div>
        </div>

        <b-skeleton v-if="isFetching" height="500" active />
        <div v-else>
          <section class="is-relative">
            <div class="box">
              <header class="columns">
                <div class="column">
                  <h2 class="title is-5 mb-0">
                    Votre abonnement
                  </h2>
                </div>
                <div class="column is-narrow">
                  <p class="tag is-primary is-medium" v-t="`packs.${authStore.plan}`" />
                  <p v-if="subscription.store_subscription" class="tag is-outlined is-medium ml-3" style="text-transform: capitalize;"
                    v-t="`plan_frequencies.${subscription.store_subscription.frequency}`" />
                </div>
              </header>
              <template v-if="subscription.store_subscription">
                <p v-if="subscription.store_subscription.last_payment_failed" class="notification is-danger mt-5 mb-0">
                  Dernier paiement échoué
                </p>
                <table class="table is-bordered mt-5">
                  <tr>
                    <th>
                      Produit
                    </th>
                    <th class="has-text-right">
                      Prix
                    </th>
                  </tr>
                  <template v-if="subscription.additional_data.stripe_subscription">
                    <tr v-for="item in subscription.additional_data.stripe_subscription.items" :key="item.id">
                      <td>
                        <span v-t="`subscription_items.${item.type}`" />
                        <template v-if="item.quantity > 1">
                          (x{{ item.quantity }})
                        </template>
                      </td>

                      <td class="has-text-right" width="128">
                        <template v-if="hasDiscountOnSubscription">
                          <span v-if="subscription.additional_data.stripe_subscription.discount.percent_off > 0" class="ml-2">{{ item.price.unit_amount * item.quantity / 100 * (1 - subscription.additional_data.stripe_subscription.discount.percent_off/100) | formatPrice }}</span>
                          <span v-else-if="subscription.additional_data.stripe_subscription.discount.amount_off > 0" class="ml-2">{{ (item.price.unit_amount * item.quantity - subscription.additional_data.stripe_subscription.discount.amount_off) / 100 | formatPrice }}</span>
                          <s class="has-text-danger ml-1">{{ item.price.unit_amount * item.quantity / 100 | formatPrice }}</s>
                        </template>
                        <template v-else>
                          {{ item.price.unit_amount * item.quantity / 100 | formatPrice }}
                        </template>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr v-for="item in subscription.store_subscription.items" :key="item.id">
                      <td>
                        <span v-t="`subscription_items.${item.type}`" />
                        <template v-if="item.quantity > 1">
                          (x{{ item.quantity }})
                        </template>
                      </td>

                      <td class="has-text-right" width="128">
                        <template v-if="hasDiscountOnSubscription">
                          <span class="ml-2">{{ item.amount * (1 - subscription.additional_data.percent_off/100) | formatPrice }}</span>
                          <s class="has-text-danger ml-1">{{ item.amount | formatPrice }}</s>
                        </template>
                        <template v-else>
                          {{ item.amount | formatPrice }}
                        </template>
                      </td>
                    </tr>
                  </template>
                </table>
                <p class="columns">
                  <span class="column">
                    <strong>Démarré le : </strong>
                    {{ subscription.store_subscription.created_at | momentFromUTC | moment('DD/MM/YY') }}
                  </span>
                  <span class="column is-narrow break-words">
                    <strong>Prochain paiement : </strong>
                    {{ subscription.additional_data.next_payment_amount | formatPrice }} •
                    {{ subscription.additional_data.next_payment_date | momentFromUTC | moment('[le] DD/MM/YY') }}
                  </span>
                </p>
              </template>
<!--              <div v-if="hasPercentPlan" class="notification is-info is-light mt-5">-->
<!--                  <div class="level is-mobile">-->
<!--                    <div class="level-left">-->
<!--                      <div>-->
<!--                        <p>-->
<!--                          <span class="title is-5">{{ authStore.customer_count }} / {{ authStore.capabilities.nb_customers }}</span>-->
<!--                          <span class="ml-2">apprenants</span>-->
<!--                        </p>-->
<!--                      </div>-->
<!--                    </div>-->
<!--                    <div class="level-right">-->
<!--                      <b-progress-->
<!--                        type="is-info"-->
<!--                        :value="authStore.customer_count"-->
<!--                        :max="authStore.capabilities.nb_customers"-->
<!--                        show-value-->
<!--                        format="percent"-->
<!--                      />-->
<!--                    </div>-->
<!--                  </div>-->
<!--                  <p class="is-size-8">-->
<!--                    Le forfait DÉCOUVERTE est limité à {{ authStore.capabilities.nb_customers }} apprenants-->
<!--                    (tous statuts confondus : inscrits, bloqués, supprimés, paniers abandonnés et retirés des formations).-->
<!--                    Passez à un forfait supérieur pour un nombre d’apprenants illimité.-->
<!--                  </p>-->
<!--                </div>-->
            </div>

            <p v-if="subscriptionPlan" class="has-text-right mb-5">
              <b-button class="has-text-danger" type="is-text" size="is-small" @click="unsubscribeStorePlan">
                Résilier l'abonnement <span v-t="`plans.${subscriptionPlan.type}`" />
              </b-button>
            </p>
          </section>

          <div v-if="paymentMethods.length" class="box mt-5">
            <h2 class="title is-5">
              Votre carte bancaire
            </h2>
            <div class="columns is-size-6">
              <div class="column is-9">
                <b-field label="Numéro">
                  <b-input :value="`XXXX XXXX XXXX ${paymentMethods[0].card_last4}`" readonly />
                </b-field>
              </div>
              <div class="column is-3">
                <b-field label="Expiration">
                  <b-input :value="paymentMethodExp" readonly />
                </b-field>
              </div>
            </div>
            <p class="has-text-right">
              <b-button type="is-text" class="tdecoration-underline px-0" @click="isChangePaymentMethodOpen = true">
                Changer ma carte
              </b-button>
            </p>
            <b-modal width="640px" :active.sync="isChangePaymentMethodOpen">
              <UpdatePaymentMethod :paymentMethod="paymentMethods[0]" @success="onPaymentMethodChange" />
            </b-modal>
          </div>

          <form ref="payment_form" class="box mt-5" @submit.prevent="dataIsValid(isLoading) && handle()">
            <h3 class="title is-5">
              <template v-if="subscriptionStartDate">
                Changer d'offre
              </template>
              <template v-else>
                Sélectionnez une offre
              </template>
            </h3>
            <template v-if="!subscriptionFrequency">
              <b-field label="Choisissez votre rythme de facturation">
                <b-radio name="frequency" native-value="MONTHLY" :value="frequency" @input="switchFrequency('MONTHLY')">
                  Mensuel
                </b-radio>
                <b-radio name="frequency" native-value="YEARLY" :value="frequency" @input="switchFrequency('YEARLY')">
                  Annuel <span class="tag is-outlined ml-3">-17%</span>
                </b-radio>
              </b-field>
              <hr>
            </template>
            <b-field label="Choisissez votre forfait">
              <div class="columns">
                <div class="column pr-1">
                  <button class="box p-3 w-full outline-none has-text-left is-size-4 is-size-6-mobile cursor-pointer"
                    :class="{
                      'border-0': plan != 'PRO',
                      'is-primary': plan == 'PRO'
                    }" type="button" @click="switchPlan('PRO')">
                    <strong class="is-block is-size-6 mb-3">
                      Forfait PRO
                    </strong>
                    {{ $constants.PRICES[frequency].PRO }} &euro;
                    <span class="is-size-6">
                      TTC / <span v-t="`plan_frequencies.PER_${frequency}`" />
                    </span>
                    <span class="is-block mt-2 is-size-7 is-size-8-mobile">
                      sans engagement
                      <br>
                      sans surcoût
                    </span>
                  </button>
                </div>
                <div class="column pl-1">
                  <button class="box p-3 w-full outline-none has-text-left is-size-4 is-size-6-mobile cursor-pointer"
                    :class="{
                      'border-0': plan != 'EXPERT',
                      'is-primary': plan == 'EXPERT'
                    }" type="button" @click="switchPlan('EXPERT')">
                    <strong class="is-block is-size-6 mb-3">
                      Forfait EXPERT
                    </strong>
                    {{ $constants.PRICES[frequency].EXPERT }} &euro;
                    <span class="is-size-6">
                      TTC / <span v-t="`plan_frequencies.PER_${frequency}`" />
                    </span>
                    <span class="is-block mt-2 is-size-7 is-size-8-mobile">
                      sans engagement
                      <br>
                      sans surcoût
                    </span>
                  </button>
                </div>
              </div>
            </b-field>
            <div v-if="frequency == 'YEARLY'"
              class="field is-block has-text-weight-bold is-size-6 is-size-7-mobile has-text-primary">
              Au lieu de {{ $constants.PRICES.MONTHLY[plan] * 12 }} &euro; / an, soit 2 mois offerts !
            </div>
            <div v-if="hasDiscountOnSubscription" class="notification">
              Votre abonnement bénéficie actuellement d'une réduction,
              le prix de ce forfait vous revient donc à
              <template v-if="subscription.additional_data.amount_off > 0">
                {{ (price - subscription.additional_data.amount_off) | formatPrice }}
                (-{{ subscription.additional_data.amount_off | formatPrice }})
              </template>
              <template v-else-if="subscription.additional_data.percent_off > 0">
                {{ (price * (1 - subscription.additional_data.percent_off / 100)) | formatPrice }}
                (-{{ subscription.additional_data.percent_off }}%)
              </template>
            </div>
            <b-field v-if="!subscriptionPlan" :addons="false">
              <template #label>
                Code promo
              </template>
              <b-input v-model.trim="discount" :loading="discountIsChecking" maxlength="32" :has-counter="false"
                       @input="checkDiscount" />
              <p v-if="discountData" class="mt-3 has-text-weight-bold"
                :class="!hasValidNewDiscount ? 'has-text-danger' : 'has-text-success'">
                <template v-if="discountData.amount_off > 0">
                  {{ (price - discountData.amount_off) | formatPrice }}
                  ({{ discountData.amount_off | formatPrice }} de réduction)
                </template>
                <template v-else-if="discountData.percent_off > 0">
                  {{ (price * (1 - discountData.percent_off / 100)) | formatPrice }}
                  ({{ discountData.percent_off }} % de réduction)
                </template>
                <template v-else-if="!hasValidNewDiscount">
                  Ce code promo n'est pas utilisable.
                </template>
              </p>
            </b-field>
            <b-field v-if="!paymentMethods.length" label="Renseignez votre carte bancaire"
              :type="{ 'is-danger': stripeError && stripeError.message }" :message="stripeError && stripeError.message">
              <StripeElementsSplitted ref="card" @error="stripeError = $event" @complete="stripeIsCompleted = $event" />
            </b-field>
            <div class="mt-5">
              <p v-if="subscriptionPlan && subscriptionPlan.type == plan"
                class="field has-text-weight-bold has-text-centered">
                C'est votre abonnement actuel.
              </p>
              <template v-else>
                <b-field class="has-text-right">
                  <b-button type="is-primary" native-type="submit" :loading="isLoading" :disabled="cantSubscribe"
                    expanded>
                    <span v-if="authUser.stripe_subscription_customer_id">
                      S'abonner au Forfait {{ plan }}
                    </span>
                    <span v-else>
                      Souscrire au Forfait {{ plan }}
                    </span>
                  </b-button>
                </b-field>
                <p class="is-size-8 has-text-centered has-text-primary">
                  <b-icon icon="shield-alt" />
                  Paiement sécurisé par SSL
                </p>
              </template>
            </div>
          </form>
        </div>
      </template>
    </AppFeatureWrapper>

    <section v-else-if="$route.query.onglet == 'services'">

      <div class="columns is-desktop">
        <!-- Offer 1 : Consultation pédagogique -->
        <div class="column">
          <div class="box content is-medium w-640 mx-auto hover-border" style="background-color: #FFF; color: #00685A;">
            <div class="columns">
              <div class="column">
                <div class="tags mb-3">
                  <span class="tag is-primary">Offre 1</span>
                </div>
                <h3 class="title is-4 mb-4" style="color: #00685A;">
                  Consultation pédagogique
                </h3>

                <p>Besoin d'un coup de pouce ponctuel ?<br>
                Un regard expert sur votre problématique spécifique ?</p>

                <p class="has-text-weight-bold">Réservez une consultation individuelle de 30 minutes à 1 heure avec notre ingénieure pédagogique experte, membre de l'équipe Teachizy.</p>

                <div class="mt-5">
                  <b-button tag="a" type="is-primary" @click="contacterEquipePeda">
                    Contacter notre équipe
                  </b-button>
                </div>

                <p class="mt-4"><small>Prix : à partir de 50 € HT</small></p>
              </div>
            </div>

            <div class="mt-5">
              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    <strong>Obtenir un retour d'expert</strong> sur votre formation.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    <strong>Optimiser vos contenus</strong> pédagogiques.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    <strong>Vous former et intégrer d'autres outils</strong> à votre formation comme Calendly ou Genially
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column">
          <div class="box content is-medium w-640 mx-auto hover-border" style="background-color: #FFF; color: #00685A;">
            <div class="columns">
              <div class="column">
                <div class="tags mb-3">
                  <span class="tag is-primary">Offre 2</span>
                </div>
                <h3 class="title is-4 mb-4" style="color: #00685A;">
                  Consultation communication & marketing
                </h3>

                <p>Besoin de clarifier votre positionnement ?<br>
                  Vous avez une question sur votre stratégie de visibilité, vos offres ou vos canaux de communication ?</p>

                <p class="has-text-weight-bold">Réservez une consultation individuelle de 30 minutes à 1 heure, avec notre experte en stratégie digitale, associée de Teachizy.</p>

                <div class="mt-5">
                  <b-button tag="a" type="is-primary" @click="contacterEquipeMarket">
                    Contacter notre équipe
                  </b-button>
                </div>

                <p class="mt-4"><small>Prix : à partir de 50 € HT</small></p>
              </div>
            </div>

            <div class="mt-5">
              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    Définir ou ajuster <strong>votre promesse et votre positionnement</strong>.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    <strong>Booster votre visibilité</strong> sur les réseaux sociaux ou via votre newsletter.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p class="is-size-6" style="color: #00685A;">
                    Améliorer vos <strong>taux de conversion</strong> et vos <strong>ventes</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Offer 3 : Audit de lancement complet -->
        <div class="column">
          <div class="box content is-medium w-640 mx-auto hover-border" style="background-color: #FFF; color: #00685A;">
            <div class="columns">
              <div class="column">
                <div class="tags mb-3">
                  <span class="tag is-primary">Offre 3</span>
                </div>
                <h3 class="title is-4 mb-4" style="color: #00685A;">
                  Audit de lancement complet
                </h3>

                <p>Sécurisez votre lancement de formation !</p>

                <p>Notre audit de lancement vous propose une analyse complète pour un démarrage optimal.</p>

                <p class="has-text-weight-bold mt-4">Comment ça marche ?</p>
                <ol>
                  <li>Réservez votre audit (5 audits par mois seulement !).</li>
                  <li>Échangez avec notre experte pour finaliser votre stratégie.</li>
                </ol>

                <div class="mt-5">
                  <b-button tag="a" type="is-primary" href="https://calendly.com/teachizy-et-vous/audit-de-lancement" target="_blank" rel="noreferrer noopener">
                    Je réserve mon audit
                  </b-button>
                  <p class="mt-4"><small>Prix : 490 € HT</small></p>
                </div>
              </div>
            </div>

            <div class="mt-5">
              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p style="color: #00685A;">
                    <strong class="is-size-6 has-text-weight-bold">Rapport détaillé :</strong> Recommandations précises pour améliorer vos contenus de formation
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p style="color: #00685A;">
                    <strong class="is-size-6 has-text-weight-bold">Vidéo explicative :</strong> Retour personnalisé illustré sur les points clés à améliorer.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p style="color: #00685A;">
                    <strong class="is-size-6 has-text-weight-bold">Échange individuel :</strong> Session dédiée pour clarifier les recommandations.
                  </p>
                </div>
              </div>

              <div class="box mb-4 has-text-dark" style="border: 2px solid #006064; border-radius: 12px; background-color: #F2F7F6;">
                <div class="is-flex">
                  <div class="mr-3 mt-1">
                    <span class="icon has-text-success">
                      <i class="fas fa-check-circle" style="background-color: white; border-radius: 50%;"></i>
                    </span>
                  </div>
                  <p style="color: #00685A;">
                    <strong class="is-size-6 has-text-weight-bold">Bonus :</strong> Mémo de lancement & la formation "garantir la qualité grâce aux bêta-tests"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-else>
      <ListFeatures :has-cta="true" />
      <div v-if="hasPercentPlan" class="content has-text-centered">
        <p>
          <b-button tag="router-link" type="is-primary" size="is-large" :to="{ query: { onglet: 'vous' } }">
            Débloquer toutes les fonctionnalités !
          </b-button>
        </p>
        <p>Pour profiter à 100% de Teachizy</p>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.pcomp {
  .hover-border {
    transition: border-color 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      border-color: #00685A;
    }
  }
}
</style>
